// Sleep Metrics Command Center
import { useState, useMemo, useCallback, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from './auth';
import { LoginScreen } from './components';
import { C, COACHING_EMAIL, AIRVIEW_BASE, ADMIN_GROUP, RC_CLIENT_ID, COACHING_PHONE, ACTION_GROUPS } from './config';
import { TODAY_STR, DEFAULT_TEMPLATES, usePatients } from './data';
import { sendSMS, initiateCall } from './services/ringcentral';
import ContactModal from './components/ContactModal';
import { PatientCard, ListTab } from './components/PatientCard';
import AdminPage from './components/AdminPage';
import { Pill, UsageDots } from './components/Atoms';
import AgentStatusPanel from './components/AgentStatusPanel';

async function gmailGetThread(patientEmail) {
  await new Promise(r => setTimeout(r, 800));
  // Simulated thread — in production queries Gmail API for messages where
  // from:clinicalteam@sleepmetrics.com to:{patientEmail} OR from:{patientEmail}
  return [
    { id:"m1", from:COACHING_EMAIL, to:patientEmail, date:"Feb 19, 2026 9:14am", subject:"Your CPAP therapy check-in", snippet:"Hi Jefferson, this is your Sleep Metrics coach..." },
    { id:"m2", from:patientEmail, to:COACHING_EMAIL, date:"Feb 19, 2026 2:31pm", subject:"Re: Your CPAP therapy check-in", snippet:"Thanks for reaching out! I've been having trouble with..." },
  ];
}


// ─── GOOGLE SSO ───────────────────────────────────────────────────────────────
// In production: uses Google Identity Services library loaded via script tag
// Checks group membership via Google Admin Directory API
// Here we simulate the flow so the UI is fully built and ready to wire up

// ─── SHARED UI ────────────────────────────────────────────────────────────────


function StatCard({ value, label, sub, color, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} style={{
      flex:1, minWidth:130, background: hov&&onClick ? color+"08" : C.surface,
      border:`1px solid ${hov&&onClick ? color+"45" : C.border}`,
      borderTop:`3px solid ${color}`, borderRadius:12, padding:"16px 18px",
      cursor:onClick?"pointer":"default", transition:"all 0.15s", userSelect:"none",
    }}>
      <div style={{ fontSize:32, fontWeight:800, color, fontFamily:"'IBM Plex Mono',monospace", lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:11, fontWeight:700, color:C.text, marginTop:6 }}>{label}</div>
      <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{sub}</div>
      {onClick && <div style={{ fontSize:10, color, marginTop:6, fontWeight:600 }}>View list →</div>}
    </div>
  );
}

// ─── EMAIL THREAD PANEL ───────────────────────────────────────────────────────
function EmailThreadPanel({ patient, templates, coachName, onSent }) {
  const [thread,   setThread]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [tmplId,   setTmplId]   = useState(() => autoSelectTemplate(patient, templates)?.id);
  const [subject,  setSubject]  = useState(`CPAP Therapy Check-in — ${patient.first} ${patient.last}`);
  const [body,     setBody]     = useState(() => {
    const t = autoSelectTemplate(patient, templates);
    return t ? renderTemplate(t.body, patient) : "";
  });
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);

  useEffect(() => {
    if (!patient.email) return;
    setLoading(true);
    gmailGetThread(patient.email).then(t => { setThread(t); setLoading(false); });
  }, [patient.email]);

  const selectTemplate = (id) => {
    setTmplId(id);
    const t = templates.find(t => t.id === id);
    if (t) setBody(renderTemplate(t.body, patient));
  };

  const handleSend = async () => {
    setSending(true);
    const latestThreadId = thread?.[0]?.threadId;
    const r = await gmailSend({ to: patient.email, subject, body, threadId: latestThreadId });
    setSending(false);
    if (r.success) {
      setSent(true);
      const note = generateBrighttreeNote(patient, "Email", body, "Email sent via clinicalteam@sleepmetrics.com", coachName);
      onSent(patient.id, { type:"Email", message:body, outcome:"Email sent", note });
    }
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

      {/* Thread history */}
      <div>
        <div style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>
          📬 Email Thread ({patient.email})
        </div>
        {loading ? (
          <div style={{ fontSize:12, color:C.muted, padding:"12px 0" }}>Loading thread…</div>
        ) : !patient.email ? (
          <div style={{ fontSize:12, color:C.red, padding:"8px 12px", background:C.red+"10", borderRadius:7 }}>⚠ No email address on file for this patient.</div>
        ) : thread?.length === 0 ? (
          <div style={{ fontSize:12, color:C.muted, padding:"8px 12px", background:C.card, borderRadius:7, border:`1px solid ${C.border}` }}>No previous emails with this patient.</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:160, overflowY:"auto" }}>
            {thread?.map(m => (
              <div key={m.id} style={{
                padding:"9px 12px", borderRadius:8,
                background: m.from === COACHING_EMAIL ? C.teal+"0C" : C.card,
                border:`1px solid ${m.from === COACHING_EMAIL ? C.teal+"28" : C.border}`,
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                  <span style={{ fontSize:11, fontWeight:700, color: m.from === COACHING_EMAIL ? C.teal : C.text }}>
                    {m.from === COACHING_EMAIL ? `You (${COACHING_EMAIL})` : m.from}
                  </span>
                  <span style={{ fontSize:10, color:C.muted }}>{m.date}</span>
                </div>
                <div style={{ fontSize:11, color:C.muted }}>{m.snippet}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compose */}
      <div>
        <div style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:7 }}>Compose New Email</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
          {templates.map(t => (
            <button key={t.id} onClick={() => selectTemplate(t.id)} style={{
              background: tmplId===t.id ? C.teal+"16" : C.card,
              border:`1px solid ${tmplId===t.id ? C.teal+"50" : C.border}`,
              color: tmplId===t.id ? C.teal : C.muted,
              borderRadius:5, padding:"3px 9px", fontSize:11,
              cursor:"pointer", fontFamily:"inherit", fontWeight:600,
            }}>{t.label}</button>
          ))}
        </div>
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Subject</div>
          <input value={subject} onChange={e=>setSubject(e.target.value)} style={{
            width:"100%", background:C.card, border:`1px solid ${C.border}`,
            borderRadius:7, padding:"8px 12px", fontSize:13,
            fontFamily:"inherit", outline:"none", color:C.text,
          }} />
        </div>
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>
            Message · Sending from <strong style={{ color:C.teal }}>{COACHING_EMAIL}</strong>
          </div>
          <textarea value={body} onChange={e=>setBody(e.target.value)} rows={5} style={{
            width:"100%", background:C.card, border:`1px solid ${C.border}`,
            borderRadius:7, padding:"10px 12px", fontSize:13, fontFamily:"inherit",
            outline:"none", resize:"vertical", color:C.text, lineHeight:1.6,
          }} />
        </div>
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end" }}>
        {!sent ? (
          <button onClick={handleSend} disabled={sending || !patient.email} style={{
            background:`linear-gradient(135deg, ${C.teal}, ${C.blue})`,
            border:"none", color:"#fff", borderRadius:8, padding:"10px 22px",
            fontSize:13, fontWeight:700, cursor:sending?"wait":"pointer",
            fontFamily:"inherit", opacity: !patient.email ? 0.4 : 1,
          }}>{sending ? "Sending…" : `Send via ${COACHING_EMAIL}`}</button>
        ) : (
          <div style={{ background:C.green+"16", border:`1px solid ${C.green}38`, color:C.green, borderRadius:8, padding:"10px 20px", fontSize:13, fontWeight:700 }}>
            ✓ Email sent · Brightree note ready to copy
          </div>
        )}
      </div>
    </div>
  );
}




// ─── CONTACT MODAL ────────────────────────────────────────────────────────────

// ─── ADMIN PAGE ───────────────────────────────────────────────────────────────

// ─── PATIENT CARD ─────────────────────────────────────────────────────────────

// ─── LIST TAB ─────────────────────────────────────────────────────────────────

// ─── AGENT STATUS PANEL ───────────────────────────────────────────────────────

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [activeTab,     setActiveTab]     = useState("overview");
  const [activePatient, setActivePatient] = useState(null);
  const [coachName,     setCoachName]     = useState("Sleep Metrics Coach");
  const [templates,     setTemplates]     = useState(DEFAULT_TEMPLATES);
  const [showAdmin,     setShowAdmin]     = useState(false);
  const [log,           setLog]           = useState({});
  const { user, authError, onSuccess, onError, signOut } = useAuth();
  const { groups, counts, updatedAt, loading, error } = usePatients();

  const addEntry = useCallback((patientId, entry) => {
    setLog(prev => ({ ...prev, [patientId]: [...(prev[patientId]||[]), { ...entry, ts:new Date().toLocaleString() }] }));
  }, []);

  const totalContacted      = Object.values(log).filter(v=>v.length>0).length;
  // "Critical" = the #1 priority group (Compliance Risk) with zero 4hr nights, not yet contacted.
  const complianceRisk      = groups['US_ComplianceRisk'] || [];
  const criticalUncontacted = complianceRisk.filter(p=>p.days4hr===0&&!log[p.id]?.length).length;
  const navTo = (tab) => { setActiveTab(tab); setShowAdmin(false); };

  const tabs = [
    { id:"overview", label:"Overview" },
    ...ACTION_GROUPS.map(g => ({ id:g.key, label:`${g.icon} ${g.label}  ${counts[g.key]||0}` })),
    { id:"agents",   label:"⚙️ Agents" },
  ];

  // Login gate  must be after all hooks
  if (!user) {
    return <LoginScreen onSuccess={onSuccess} onError={onError} authError={authError} />;
  }

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700;9..40,800&family=IBM+Plex+Mono:wght@400;500;700&display=swap');
        * { box-sizing:border-box; }
        ::-webkit-scrollbar { width:4px; background:transparent; }
        ::-webkit-scrollbar-thumb { background:#C8CEDB; border-radius:4px; }
        a { transition:opacity 0.12s; } a:hover { opacity:0.78; }
        button { transition:opacity 0.12s; } button:hover:not(:disabled) { opacity:0.82; }
        select option { background:#fff; color:#141928; }
      `}</style>

      {/* Top bar */}
      <div style={{ height:52, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 24px", background:C.surface, position:"sticky", top:0, zIndex:200, boxShadow:"0 1px 4px #00000010" }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ background:`linear-gradient(135deg, ${C.teal}, ${C.blue})`, borderRadius:7, padding:"4px 10px", fontSize:12, fontWeight:800, color:"#fff" }}>SM</div>
          <span style={{ fontSize:13, fontWeight:700, color:C.text }}>Sleep Coach Command Center</span>
          <span style={{ fontSize:11, color:C.muted }}>AirView · RingCentral · Gmail · Brightree</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          {criticalUncontacted > 0 && (
            <div onClick={()=>navTo("listA")} style={{ background:C.red+"12", border:`1px solid ${C.red}28`, color:C.red, borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700, fontFamily:"'IBM Plex Mono',monospace", cursor:"pointer" }}>
              ⚡ {criticalUncontacted} critical
            </div>
          )}

          {/* Google SSO */}
          {user ? (
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:C.teal+"22", border:`1px solid ${C.teal}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:C.teal }}>
                {user.name.charAt(0)}
              </div>
              <div style={{ lineHeight:1.2 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.text }}>{user.name}</div>
                {user.isAdmin && <div style={{ fontSize:9, color:C.green, fontWeight:700 }}>ADMIN</div>}
              </div>
              <button onClick={()=>setShowAdmin(true)} style={{ background:C.card, border:`1px solid ${C.border}`, color:C.muted, borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>⚙️ Admin</button>
              <button onClick={signOut} style={{ background:"none", border:"none", color:C.muted, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Sign out</button>
            </div>
          ) : (
            <button onClick={signIn} disabled={loading} style={{ background:`linear-gradient(135deg, ${C.teal}, ${C.blue})`, border:"none", color:"#fff", borderRadius:7, padding:"7px 16px", fontSize:12, fontWeight:700, cursor:loading?"wait":"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:8 }}>
              {loading ? "Signing in…" : "🔐 Sign in with Google"}
            </button>
          )}

          <input value={coachName} onChange={e=>setCoachName(e.target.value)} placeholder="Your name" style={{ background:C.card, border:`1px solid ${C.border}`, color:C.muted, borderRadius:6, padding:"5px 10px", fontSize:11, fontFamily:"inherit", outline:"none", width:160 }} />
          <span style={{ fontSize:11, color:C.muted, fontFamily:"'IBM Plex Mono',monospace" }}>
            {new Date().toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}
          </span>
        </div>
      </div>

      {/* Nav tabs */}
      {!showAdmin && (
        <div style={{ display:"flex", gap:2, padding:"0 24px", borderBottom:`1px solid ${C.border}`, background:C.surface, overflowX:"auto" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={()=>navTo(t.id)} style={{
              background:"none", border:"none", whiteSpace:"nowrap", flexShrink:0,
              borderBottom: activeTab===t.id ? `2px solid ${C.teal}` : "2px solid transparent",
              color: activeTab===t.id ? C.text : C.muted,
              padding:"11px 16px", fontSize:12, fontWeight:activeTab===t.id?700:500,
              cursor:"pointer", fontFamily:"inherit", marginBottom:-1,
            }}>{t.label}</button>
          ))}
        </div>
      )}

      <div style={{ padding:"22px 24px", maxWidth:1360, margin:"0 auto" }}>

        {showAdmin && <AdminPage templates={templates} setTemplates={setTemplates} onBack={()=>setShowAdmin(false)} user={user} />}

        {!showAdmin && loading && (
          <div style={{ padding:"60px 0", textAlign:"center", color:C.muted, fontSize:14 }}>⏳ Loading live patient data…</div>
        )}
        {!showAdmin && !loading && error && (
          <div style={{ background:C.red+"10", border:`1px solid ${C.red}38`, color:C.red, borderRadius:10, padding:"16px 20px", fontSize:13 }}>⚠ {error}</div>
        )}

        {!showAdmin && !loading && !error && activeTab==="overview" && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            {updatedAt && <div style={{ fontSize:11, color:C.muted }}>Live data · updated {new Date(updatedAt).toLocaleString()}</div>}
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              {ACTION_GROUPS.slice(0,6).map(g => (
                <StatCard key={g.key} value={counts[g.key]||0} label={g.label} sub={g.short} color={g.color} onClick={()=>navTo(g.key)} />
              ))}
              <StatCard value={totalContacted}      label="Contacted This Week"  sub="across all groups"                 color={C.green} onClick={null} />
              <StatCard value={criticalUncontacted} label="Critical Uncontacted" sub="Compliance Risk · zero 4hr nights" color={C.red}   onClick={()=>navTo("US_ComplianceRisk")} />
            </div>

            {/* Integration status */}
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderLeft:`3px solid ${C.green}`, borderRadius:10, padding:"14px 18px" }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.green, marginBottom:10 }}>✓ Integrations</div>
              <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
                {[
                  { label:"RingCentral SMS",   status:"Live",             color:C.green  },
                  { label:"RingCentral Call",  status:"Pending Approval", color:C.orange },
                  { label:"Gmail Send",        status:"Live",             color:C.green  },
                  { label:"Gmail Receive",     status:"Live",             color:C.green  },
                  { label:"Google SSO",        status:"Live",             color:C.green  },
                  { label:"Brightree Agent",   status:"Ready",            color:C.blue   },
                  { label:"AirView Agent",     status:"Ready",            color:C.blue   },
                ].map(s => (
                  <div key={s.label} style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <div style={{ width:6, height:6, borderRadius:"50%", background:s.color }} />
                    <span style={{ fontSize:11, color:C.muted }}>{s.label}</span>
                    <span style={{ fontSize:10, color:s.color, fontWeight:700 }}>{s.status}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Workflow */}
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:"18px 20px" }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:14 }}>Coaching Workflow — 5 Steps</div>
              <div style={{ display:"flex", gap:0 }}>
                {[
                  { title:"Open List",         desc:"List A is your daily queue — sorted by urgency.",               icon:"📋", color:C.red    },
                  { title:"Review in AirView", desc:"Click 'AirView ↗' — opens patient chart directly.",            icon:"📊", color:C.orange },
                  { title:"Contact Patient",   desc:"SMS via RingCentral, email via Gmail, or log a call.",          icon:"💬", color:C.teal   },
                  { title:"Log Outcome",       desc:"Select result. Brightree note auto-generates.",                  icon:"✏️", color:C.blue   },
                  { title:"Push to Brightree", desc:"One click — agent pastes note into patient file automatically.", icon:"✅", color:C.green  },
                ].map((step,i) => (
                  <div key={i} style={{ flex:1, textAlign:"center", position:"relative", padding:"0 8px" }}>
                    <div style={{ width:36, height:36, borderRadius:"50%", background:step.color+"16", border:`2px solid ${step.color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, margin:"0 auto 8px" }}>{step.icon}</div>
                    <div style={{ fontSize:11, fontWeight:700, color:C.text, marginBottom:3 }}>{step.title}</div>
                    <div style={{ fontSize:10, color:C.muted, lineHeight:1.5 }}>{step.desc}</div>
                    {i<4 && <div style={{ position:"absolute", right:-4, top:14, color:C.faint, fontSize:18 }}>›</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Today's call list */}
            <div style={{ background:C.surface, border:`1px solid ${C.red}20`, borderRadius:12, padding:"16px 18px" }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.red, marginBottom:12 }}>🔴 Call These Patients Today <span style={{ fontWeight:500, color:C.muted }}>· Compliance Risk, zero 4hr nights</span></div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {complianceRisk.filter(p=>p.days4hr===0&&!log[p.id]?.length&&p.phone).slice(0,6).map(p=>(
                  <div key={p.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 12px", background:C.card, border:`1px solid ${C.border}`, borderRadius:8 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:C.text, flex:1 }}>{p.first} {p.last}</span>
                    <span style={{ fontSize:11, color:C.muted }}>Day {p.days}</span>
                    <span style={{ fontSize:11, fontFamily:"'IBM Plex Mono',monospace", color:C.muted }}>{p.phone.replace(/(\d{3})(\d{3})(\d{4})/,"$1-$2-$3")}</span>
                    <a href={`${AIRVIEW_BASE}/${p.ecoId || p.airviewId}`} target="_blank" rel="noreferrer"
                      style={{ fontSize:11, color:C.teal, background:C.teal+"10", border:`1px solid ${C.teal}25`, padding:"4px 10px", borderRadius:5, textDecoration:"none", fontWeight:600 }}>AirView ↗</a>
                    <button onClick={()=>setActivePatient(p)} style={{ background:C.teal+"14", border:`1px solid ${C.teal}30`, color:C.teal, borderRadius:6, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Contact →</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!showAdmin && !loading && !error && ACTION_GROUPS.map(g => activeTab===g.key && (
          <ListTab key={g.key} title={g.label} subtitle={g.short} data={groups[g.key]||[]} listType={g.key} color={g.color} icon={g.icon} log={log} onContact={setActivePatient} />
        ))}
        {!showAdmin && activeTab==="agents" && (
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
            <div>
              <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:4 }}>⚙️ Automation Agents</div>
              <div style={{ fontSize:12, color:C.muted }}>Monitor and manually trigger the Brightree and AirView agents. In production these run automatically.</div>
            </div>
            <AgentStatusPanel />
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 18px" }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:12 }}>📋 Deployment Checklist</div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {[
                  { done:true,  label:"RingCentral app created",                          detail:"Client ID configured · SMS live" },
                  { done:true,  label:"Gmail MCP connected",                              detail:"clinicalteam@sleepmetrics.com authorized" },
                  { done:false, label:"Google Cloud Console — OAuth Client ID",           detail:"Set GOOGLE_CLIENT_ID in config · Required for SSO" },
                  { done:false, label:"Google Admin — create dashboard-admins group",     detail:"Add admin users · Required for admin gate" },
                  { done:false, label:"Brightree agent account created",                  detail:"agent.notes@sleepmetrics.com · Full notes access" },
                  { done:false, label:"AirView agent account created",                    detail:"Dedicated credentials · Compliance export access" },
                  { done:true, label:"Deployed to Google Cloud Run",                   detail:"GCP Cloud Run  sleep-metrics-production  coach.sleepmetrics.com" },
                  { done:false, label:"RingCentral Call Control approved",                detail:"Pending RingCentral review · Enables one-click dial" },
                ].map((item,i) => (
                  <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"8px 12px", background:C.surface, border:`1px solid ${item.done?C.green+"28":C.border}`, borderRadius:8 }}>
                    <div style={{ width:18, height:18, borderRadius:"50%", background:item.done?C.green+"18":C.faint, border:`1px solid ${item.done?C.green+"40":C.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, flexShrink:0, marginTop:1 }}>
                      {item.done ? <span style={{ color:C.green }}>✓</span> : <span style={{ color:C.muted }}>○</span>}
                    </div>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:item.done?C.green:C.text }}>{item.label}</div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{item.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {activePatient && (
        <ContactModal
          patient={activePatient} coachName={coachName} templates={templates}
          onClose={()=>setActivePatient(null)}
          onSent={(id,entry)=>{ addEntry(id,entry); setActivePatient(null); }}
        />
      )}
    </div>
  );
}
